/**
 * Who else is on this document — §8.3's "presence ships in 1a".
 *
 * §8.3 justifies presence with a specific case: "meeting notes — several people
 * typing during a call". That case is also the acceptance criterion for the
 * phase ("two users see each other's presence on the same document"), so this is
 * a small component with a large obligation.
 *
 * **It never opens a connection.** `PresenceBar` takes a `nodeId` and nothing
 * else, so it cannot know whether the person is allowed to edit — and the collab
 * socket authorizes `document.edit`, not `document.view`. Creating a provider
 * here would mean every reader is refused and then reconnects forever. Instead
 * it joins the session the editor opened, which is exactly the right semantics:
 * presence is a view of who is editing, so when nobody is editing there is
 * nothing to show.
 */

import type { ReactElement } from 'react';
import { cn } from '../components/ui/cn.ts';
import { useCollabSession } from './collab-session.ts';
import { initials, type PresenceFace } from './identity.ts';

export type { PresenceFace } from './identity.ts';

/** Beyond this, the row becomes a count rather than a row. */
const MAX_FACES = 5;

function Face({ face, isSelf }: { face: PresenceFace; isSelf: boolean }): ReactElement {
  const label = isSelf ? `${face.name} (you)` : face.name;
  return (
    <span
      title={label}
      aria-label={label}
      role="img"
      className={cn(
        'inline-flex size-7 items-center justify-center rounded-full text-xs font-semibold',
        'text-white ring-2 ring-[var(--background-color-primary)]',
        isSelf && 'opacity-70',
      )}
      style={{ backgroundColor: face.color }}
    >
      {initials(face.name)}
    </span>
  );
}

export function PresenceBar({ nodeId }: { nodeId: string }): ReactElement {
  const session = useCollabSession(nodeId, { connect: false });
  const { self, others } = session;

  // No editing session on this page at all — which is the ordinary state of a
  // reader looking at the published version, not a failure. Nothing is painted,
  // but the fact is still stated for a screen reader, because "is anyone else in
  // this document" is exactly the question presence exists to answer.
  if (!self && others.length === 0) {
    return <span className="sr-only">No one is editing this document.</span>;
  }

  const shown = others.slice(0, MAX_FACES);
  const overflow = others.length - shown.length;

  return (
    // No wrapping landmark or `role`: each face is an `img` with the person's
    // name on it, and the sentence beside them says how many there are, so a
    // grouping role would add a level of nesting without adding information.
    <span className="inline-flex items-center gap-2">
      <span className="flex -space-x-1.5">
        {self ? <Face face={self} isSelf /> : null}
        {shown.map((face) => (
          <Face key={face.personId} face={face} isSelf={false} />
        ))}
        {overflow > 0 ? (
          <span
            className="inline-flex size-7 items-center justify-center rounded-full bg-tertiary text-xs font-semibold text-tertiary ring-2 ring-[var(--background-color-primary)]"
            title={others
              .slice(MAX_FACES)
              .map((face) => face.name)
              .join(', ')}
          >
            +{overflow}
          </span>
        ) : null}
      </span>
      <span className="text-xs text-tertiary">
        {others.length === 0
          ? 'Only you'
          : others.length === 1
            ? `${(others[0] as PresenceFace).name} is here`
            : `${others.length} others are here`}
      </span>
    </span>
  );
}
